import type { FC } from 'react';
import React from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { BinIcon, Button } from '@motorway/mw-highway-code';

import { DialogModal } from 'Components/shared/DialogModal';
import texts from 'Components/VehicleList/VehicleListV2/VehicleListV2.texts.json';
import { formatText } from 'Utilities/formatters';

import type { CreateViewModel, DeleteViewModel, EditViewModel, ListViewModel, ModalView } from './useSavedSearchesFlow';
import { useSavedSearchesFlow } from './useSavedSearchesFlow';
import { CreateView, DeleteView, EditView, ListView } from './views';

import styles from './SavedSearchesFlow.module.scss';

const LocalT = new MDText(texts.savedSearchModals);

const TITLES: Record<ModalView, string> = {
	closed: '',
	create: `${LocalT.translate('saveTitle')}`,
	delete: `${LocalT.translate('deleteTitle')}`,
	edit: `${LocalT.translate('editTitle')}`,
	list: `${LocalT.translate('title')}`,
};

const ListFooter: FC<{ list: ListViewModel }> = ({ list }) => (
	<div className={styles.listFooter}>
		<div className={styles.editWrapper}>
			<Button
				fullWidth
				disabled={!list.activeFilterId && !list.selectedId}
				label={`${LocalT.translate('clear')}`}
				onClick={list.onClearFilters}
				size="medium"
				variant="secondary"
			/>
		</div>
		<div className={styles.showVehiclesWrapper}>
			<Button
				fullWidth
				label={`${LocalT.translate('showVehicles', { count: formatText(list.previewCount) })}`}
				loading={list.isCountLoading}
				onClick={list.onShowVehicles}
				size="medium"
				variant="primary"
			/>
		</div>
	</div>
);

const CreateFooter: FC<{ create: CreateViewModel }> = ({ create }) => {
	const isSaveDisabled = !create.name.trim() || !!create.nameError || create.isSaving;

	return (
		<div className={styles.formFooter}>
			<Button
				fullWidth
				disabled={isSaveDisabled}
				form="create-saved-search-form"
				label={`${LocalT.translate('save')}`}
				loading={create.isSaving}
				size="medium"
				type="submit"
				variant="primary"
			/>
		</div>
	);
};

const EditFooter: FC<{ edit: EditViewModel }> = ({ edit }) => {
	const hasChanges =
		edit.editName.trim() !== edit.selectedFilter?.name || edit.isSendEmailAlert !== edit.selectedFilter?.sendEmailAlert;

	const isSaveDisabled = !edit.editName.trim() || !!edit.nameError || edit.isSaving || !hasChanges;

	return (
		<div className={styles.formFooter}>
			<Button
				fullWidth
				reverse
				icon={BinIcon}
				label={`${LocalT.translate('delete')}`}
				onClick={edit.onDelete}
				size="medium"
				variant="secondary"
			/>
			<Button
				fullWidth
				disabled={isSaveDisabled}
				form="edit-saved-search-form"
				label={`${LocalT.translate('save')}`}
				loading={edit.isSaving}
				size="medium"
				type="submit"
				variant="primary"
			/>
		</div>
	);
};

const DeleteFooter: FC<{ deleteView: DeleteViewModel }> = ({ deleteView }) => (
	<div className={styles.formFooter}>
		<Button
			fullWidth
			label={`${LocalT.translate('cancel')}`}
			onClick={deleteView.onCancel}
			size="medium"
			variant="secondary"
		/>
		<Button
			fullWidth
			disabled={deleteView.isSaving}
			label={`${LocalT.translate('confirmDelete')}`}
			loading={deleteView.isSaving}
			onClick={deleteView.onConfirm}
			size="medium"
			variant="primary"
		/>
	</div>
);

interface SavedSearchesFlowProps {
	activeFilterId?: number;
	initialView?: 'create' | 'list';
	isOpen: boolean;
	onClose: () => void;
}

export const SavedSearchesFlow: FC<SavedSearchesFlowProps> = (props) => {
	const { create, delete: deleteView, edit, list, onClose, view } = useSavedSearchesFlow(props);

	if (view === 'closed') return null;

	const footerMap: Record<Exclude<ModalView, 'closed'>, React.ReactNode> = {
		create: <CreateFooter create={create} />,
		delete: <DeleteFooter deleteView={deleteView} />,
		edit: <EditFooter edit={edit} />,
		list: <ListFooter list={list} />,
	};

	const contentMap: Record<Exclude<ModalView, 'closed'>, React.ReactNode> = {
		create: (
			<CreateView
				name={create.name}
				nameError={create.nameError}
				onNameChange={create.onNameChange}
				onSubmit={create.onSave}
				onToggleEmailAlert={create.onToggleEmailAlert}
				savedFilterValues={create.savedFilterValues}
				sendEmailAlert={create.sendEmailAlert}
			/>
		),
		delete: <DeleteView filter={deleteView.selectedFilter} savedFilterValues={deleteView.savedFilterValues} />,
		edit: (
			<EditView
				editName={edit.editName}
				isSendEmailAlert={edit.isSendEmailAlert}
				nameError={edit.nameError}
				onNameChange={edit.onNameChange}
				onSubmit={edit.onSave}
				onToggleEmailAlert={edit.onToggleEmailAlert}
				savedFilterValues={edit.savedFilterValues}
			/>
		),
		list: (
			<ListView
				onEditRow={list.onEditRow}
				onSelect={list.onSelect}
				savedFilters={list.savedFilters}
				selectedId={list.selectedId}
			/>
		),
	};

	const onBackButtonClickMap: Partial<Record<ModalView, () => void>> = {
		edit: edit.handleBackButtonClick,
	};

	return (
		<DialogModal
			isOpen
			classNames={{ content: cx({ [styles.listContent]: view === 'list' }) }}
			footer={footerMap[view]}
			onBackButtonClick={onBackButtonClickMap[view]}
			onClose={onClose}
			size="small"
			title={TITLES[view]}
		>
			{contentMap[view]}
		</DialogModal>
	);
};
